
import React, { useState, useEffect, useRef } from 'react';
import { RobotIcon, SendIcon, XIcon } from './Icons';
import { useAdmin } from '../contexts/AdminContext';
import { sendClientData } from '../services/emailService';

interface NightSupportChatProps {
    onClose: () => void;
}

interface Message {
    id: string;
    text: string;
    sender: 'bot' | 'user';
}

const NightSupportChat: React.FC<NightSupportChatProps> = ({ onClose }) => {
    const { logSubmission } = useAdmin();
    const [step, setStep] = useState(0);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<Message[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const [formData, setFormData] = useState({
        name: '',
        birthDate: '',
        phone: '',
        preferredTime: '',
        mediumPreference: ''
    });

    const addMessage = (text: string, sender: 'bot' | 'user') => {
        setMessages(prev => [...prev, { id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`, text, sender }]);
    };

    const botTyping = (text: string, delay = 1000) => {
        setIsTyping(true);
        const timer = setTimeout(() => {
            setIsTyping(false);
            addMessage(text, 'bot');
        }, delay);
        return timer;
    };

    useEffect(() => {
        const t1 = botTyping("Bonsoir. Le cabinet est actuellement fermé (horaires : 07h - 23h).", 500);
        const t2 = setTimeout(() => {
            botTyping("Je suis le Support de Nuit. Je peux enregistrer votre demande pour qu'un expert vous rappelle dès demain matin. Quel est votre prénom ?", 1500);
        }, 2000);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, []);

    useEffect(() => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    }, [messages, isTyping]);

    const handleSend = async () => {
        if (!input.trim()) return;
        
        const userText = input.trim();
        addMessage(userText, 'user');
        setInput('');

        if (step === 0) { 
            setFormData(prev => ({ ...prev, name: userText }));
            setStep(1);
            botTyping(`Enchanté ${userText}. Quelle est votre date de naissance ? (JJ/MM/AAAA)`, 1000);
        } 
        else if (step === 1) { 
            setFormData(prev => ({ ...prev, birthDate: userText }));
            setStep(2);
            botTyping("Merci. Sur quel numéro de téléphone pouvons-nous vous joindre demain ? (06...)", 1000);
        }
        else if (step === 2) { 
            const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
            if (!phoneRegex.test(userText)) {
                botTyping("Ce numéro semble invalide. Merci de renseigner un format français valide (ex: 0612345678).", 800);
                return; 
            }
            setFormData(prev => ({ ...prev, phone: userText }));
            setStep(3);
            botTyping("C'est noté. Vers quelle heure préférez-vous être appelé(e) ? (ex: 9h, 14h...)", 1000);
        }
        else if (step === 3) { 
            setFormData(prev => ({ ...prev, preferredTime: userText }));
            setStep(4);
            botTyping("Avez-vous une préférence pour un voyant particulier ? (Si non, écrivez 'Non')", 1000);
        }
        else if (step === 4) { 
            const finalData = { ...formData, mediumPreference: userText };
            setFormData(finalData);
            setStep(5);
            
            botTyping("Parfait, tout est enregistré !", 800);
            
            await logSubmission({
                type: "RDV Nuit",
                name: finalData.name,
                phone: finalData.phone,
                details: `RDV Support Nuit. Naissance: ${finalData.birthDate}. Heure souhaitée: ${finalData.preferredTime}. Pref Medium: ${userText}`
            });

            await sendClientData({
                name: finalData.name,
                phone: finalData.phone,
                birthDate: finalData.birthDate,
                subject: "RDV SUPPORT NUIT",
                details: `Le client souhaite être rappelé vers ${finalData.preferredTime}. Pref Medium: ${userText}`
            });

            setTimeout(() => {
                botTyping("Votre demande a été transmise en priorité au secrétariat. Vous serez contacté(e) demain. Douce nuit à vous.", 1000);
            }, 2000);
        }
    };

    return (
        <div className="fixed bottom-0 right-0 z-[999] w-full md:w-96 h-[500px] md:mb-4 md:mr-4 bg-gradient-to-b from-[#0f172a] to-[#1e293b] rounded-t-2xl md:rounded-2xl shadow-2xl border border-blue-500/30 flex flex-col overflow-hidden animate-slide-up max-h-[85dvh]">
            <div className="bg-blue-900/50 p-4 flex justify-between items-center border-b border-white/10">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-full border border-blue-400/50">
                        <RobotIcon className="w-6 h-6 text-blue-300" />
                    </div>
                    <div>
                        <h3 className="font-bold text-white text-sm">Support Nuit</h3>
                        <span className="text-[10px] text-blue-300 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse"></span> En ligne
                        </span>
                    </div>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-white">
                    <XIcon className="w-5 h-5" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-black/20 overscroll-contain">
                {messages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm shadow-md ${
                            msg.sender === 'user' 
                            ? 'bg-blue-600 text-white rounded-tr-none' 
                            : 'bg-slate-700 text-gray-200 rounded-tl-none border border-slate-600'
                        }`}>
                            {msg.text}
                        </div>
                    </div>
                ))}
                {isTyping && (
                    <div className="flex justify-start">
                        <div className="bg-slate-700 px-4 py-3 rounded-2xl rounded-tl-none border border-slate-600 flex gap-1">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></span>
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-100"></span>
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-200"></span>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className="p-3 bg-slate-800 border-t border-white/10">
                {step < 5 ? (
                    <div className="flex gap-2">
                        <input 
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                            placeholder="Écrivez votre réponse..."
                            className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                            enterKeyHint="send"
                            autoFocus
                        />
                        <button 
                            onClick={handleSend}
                            className="p-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                        >
                            <SendIcon className="w-5 h-5" />
                        </button>
                    </div>
                ) : (
                    <div className="text-center text-xs text-gray-400 py-2">
                        Conversation terminée. Vous pouvez fermer cette fenêtre.
                    </div>
                )}
            </div>
        </div>
    );
};

export default NightSupportChat;
